/*=== CORE IMPORTS ===*/
import { html, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { map } from 'lit/directives/map.js';
import { q1ProjectsListStyles } from './q1-projects-list-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/radio-group/radio-group.js';
import '@shoelace-style/shoelace/dist/components/radio-button/radio-button.js';

/*=== Q1 COMPONENTS ===*/
import '../q1-spinner-panel/q1-spinner-panel.js';
import '../q1-project-chip/q1-project-chip.js';

/*=== Q1 MODULES ===*/
import { enrichProject } from '../../modules/project-helpers-client.js';
import { q1MembershipFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

const FILTER_BY_STATUS_OPTIONS = [
  { label: 'All', value: 'all', description: 'Active & Proposed Projects' },
  { label: 'Active', value: 'active', description: 'Active Projects Only' },
  { label: 'Proposed', value: 'proposed', description: 'Proposed Projects Only' },
];

const SORT_FIELD_OPTIONS = [
  { label: 'Name', value: 'name', description: 'Sort by Project Name' },
  { label: 'Date', value: 'timestamp', description: 'Sort by Date' },
  { label: 'Project Key', value: 'key', description: 'Sort by Project Key' },
];

const SORT_ORDER_OPTIONS = [
  { icon: 'arrow_upward', label: 'Ascending', value: 'asc', description: 'Sort Ascending' },
  { icon: 'arrow_downward', label: 'Descending', value: 'desc', description: 'Sort Descending' },
];

/*=== COMPONENT CLASS ===*/

/**
 * Wrapper around the browseProjects function.
 * Queries and displays a list of project docs.
 */
export class Q1ProjectsList extends LitElement {
  static styles = [ q1ProjectsListStyles ];

  static properties = {
    /**
     * Firestore ref path of the subject doc.
     * Example: `projects/1234567890`
     */
    subjectRef: { type: String, attribute: 'subject' },

    /**
     * The URL path of the detail view of the subject. Used to construct the projects links.
     * Should NOT include the trailing slash.
     * Example: `/projects/acme-project-key`
     */
    subjectDetailViewPath: { type: String, attribute: 'subject-detail-view-path' },

    /**
     * The projects collection to query.
     * Example: `projects`
     */
    projectsCollection: { type: String, attribute: 'query' },

    /**
     * Optional map of all projects by ref  which can be used to enrich the project ids in the list.
     * If not included, the projects are displayed in the table as `[ Project Not Visible ]`
     */
    projectRefMap: { type: Object },

    /** Controls whether the external or internal name is displayed and other visibility. */
    isSignedIn: { type: Boolean, attribute: 'signed-in' },

    /**
     * Leave this NULL or UNDEFINED to query for projects on load.
     * Set this to an array (empty or not) to skip the query and use the provided projects as
     * the initial page of results.
     * 
     * If this is provided to the component directly, then the component will not query for the 
     * projects on load, instead waiting for the viewer to click the load more button.
     * 
     * If this IS provided, it should include the project data as well (projects.project = {...}).
     */
    projects: { type: Array },

    /**
     * The number of items to load per page.
     * Default: `20`
     */
    pageSize: { type: Number, attribute: 'page-size' },

    /**
     * Defaults to false. Controls whether filterByStatus can be toggled in the UI.
     */
    enableStatusFilter: { type: Boolean, attribute: 'enable-status-filter' },

    /**
     * Defaults to `active`. Filters projects by status.
     * 
     * Allowed values:
     * - `active`: Active projects
     * - `proposed`: Proposed projects
     * - `all`: Both active and proposed (but not inactive)
     */
    filterByStatus: { type: String, attribute: 'status' },

    /**
     * Used to track whether the first page of projects has been loaded.
     * In particular, when projects is set to a pre-queried list, this will start out as false
     * and then go to true after the first full refresh.
     */
    firstPageLoaded: { type: Boolean },

    nextPage: { type: Number },
    pageHistory: { type: Array },

    /**
     * This tracks when a server refresh is in progress.
     */
    loading: { type: Boolean },

    sortField: { type: String },
    sortOrder: { type: String },

    /**
     * Number of items to load per page.
     * Default: `20`
     */
    pageSize: { type: Number, attribute: 'page-size' },
    
    /**
     * This goes to true once the initial refresh is complete, or goes to true right away if
     * the projects prop is set on load.
     */
    initComplete: { type: Boolean },

    error: { type: Boolean },
    errorMessage: { type: String },
  };

  constructor() {
    super();

    this.subjectRef = null;
    this.subjectDetailViewPath = null;
    this.projectsCollection = null;
    this.projectRefMap = {};
    this.isSignedIn = false;

    this.projects = null;
    this.pageSize = 20;
    this.enableStatusFilter = false;
    this.filterByStatus = 'active';

    this.firstPageLoaded = false;
    this.nextPage = null;
    this.pageHistory = null;
    this.loading = false;

    this.sortField = 'timestamp';
    this.sortOrder = 'asc';

    this.initComplete = false;
    this.error = false;
    this.errorMessage = '';
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    if (this.projects) {
      this._enrichCachedProjects();
      this.initComplete = true; // first page loaded stays false for now
    } else {
      this.refreshProjects();
    }
  }

  /*=== ACTIONS ===*/

  async refreshProjects(event = null, loadMore = false) {
    const { browseMemberships } = q1MembershipFunctions;

    if (this.loading) return; // Prevent multiple loads
    this.loading = true;

    try {
      const response = await browseMemberships({
        subjectId: this.subjectId,
        subjectCollection: this.subjectCollection,
        membershipCollection: this.projectsCollection,
        queryPage: loadMore ? this.nextPage || 1 : 1,
        pageHistory: loadMore ? this.pageHistory || [] : [],
        pageSize: this.pageSize,
        sort: this.sort,
        filters: this.filters,
      });
      const { projects, nextPage, pageHistory } = response.data;

      projects.forEach(project => {
        enrichProject(project, this.projectRefMap);
      });
      this.projects = loadMore && this.firstPageLoaded
        ? [ ...this.projects, ...projects ]
        : projects;

      this.firstPageLoaded = true;
      this.nextPage = nextPage;
      this.pageHistory = pageHistory;
    } catch (error) {
      console.error('Error loading projects', error);

      this.error = true;
      this.errorMessage = error.message;
    } finally {
      this.loading = false;
      this.initComplete = true;
    }
  }

  async loadMore(event) {
    this.refreshProjects(event, true);
  }

  /*=== HANDLERS ===*/

  handleQueryChange(event) {
    const { name, value } = event.target;

    this[name] = value;

    this.refreshProjects();
  }

  /*=== GETTERS ===*/

  get subjectId() {
    if (!this.subjectRef?.includes?.('/')) return null;

    return this.subjectRef.split('/')[1];
  }
  
  get subjectCollection() {
    if (!this.subjectRef?.includes?.('/')) return null;

    return this.subjectRef.split('/')[0];
  }

  get timestampTitle() {
    if (this.filterByStatus === 'proposed') return 'Date';
    if (this.filterByStatus === 'active') return 'Joined';
    return 'Date';
  }

  get detailsTitle() {
    if (this.filterByStatus === 'proposed') return 'Proposer';
    if (this.filterByStatus === 'active') return 'Headline';
    return 'Details';
  }

  get emptyPlaceholderText() {
    if (this.filterByStatus === 'proposed') return 'There are currently no proposed projects';
    if (this.filterByStatus === 'active') return 'There are currently no active projects';
    return 'There are currently no proposed or active projects';
  }

  get sort() {
    return {
      field: this.sortField || 'timestamp',
      order: this.sortOrder || 'asc',
    };
  }

  get filters() {
    if (this.filterByStatus === 'active') return [
      { field: 'status', operator: '==', value: 'active' },
    ];

    if (this.filterByStatus === 'proposed') return [
      { field: 'status', operator: '==', value: 'proposed' },
    ];

    return [];
  }

  get showLoadMoreButton() {
    return this.isSignedIn && (this.nextPage || !this.firstPageLoaded);
  }

  /*=== TEMPLATES ===*/

  render() {
    return html`
      <div id="base-container" part="base">
        <query-bar>
          <div class="input-set">
            <span class="label hide-until-bp1">Sort:</span>

            <sl-radio-group
              name="sortField"
              size="small"
              @sl-change=${this.handleQueryChange}
              value=${this.sortField}
            >
              ${map(SORT_FIELD_OPTIONS, option => html`
                <sl-tooltip content=${option.description}>
                  <sl-radio-button value=${option.value} pill>
                    ${option.label}
                  </sl-radio-button>
                </sl-tooltip>
              `)}
            </sl-radio-group>
  
            <sl-radio-group
              name="sortOrder"
              size="small"
              @sl-change=${this.handleQueryChange}
              value=${this.sortOrder}
            >
              ${map(SORT_ORDER_OPTIONS, option => html`
                <sl-tooltip content=${option.description}>
                  <sl-radio-button value=${option.value} pill>
                    <sl-icon library="material" name=${option.icon} label=${option.label}></sl-icon>
                  </sl-radio-button>
                </sl-tooltip>
              `)}
            </sl-radio-group>
          </div>

          <div class="spacer"></div>
          
          ${!this.enableStatusFilter ? '' : html`
            <div class="input-set">
              <span class="label hide-until-bp1">Status:</span>

              <sl-radio-group
                name="filterByStatus"
                size="small"
                @sl-change=${this.handleQueryChange}
                value=${this.filterByStatus}
              >
                ${map(FILTER_BY_STATUS_OPTIONS, option => html`
                  <sl-tooltip content=${option.description}>
                    <sl-radio-button value=${option.value} pill>
                      ${option.label}
                    </sl-radio-button>
                  </sl-tooltip>
                `)}
              </sl-radio-group>
            </div>
          `}
          
          ${!this.isSignedIn ? '' : html`
            <sl-button
              @click=${this.refreshProjects}
              size="small" pill
              ?disabled=${this.loading}
            >
              ${this.loading ? html`
                <sl-spinner slot="prefix"></sl-spinner>
                Refreshing...
              ` : html`
                <sl-icon library="material" name="refresh" slot="prefix"></sl-icon>
                Refresh
              `}
            </sl-button>
          `}
        </query-bar>

        <table-wrapper>
          ${this.queryTableTemplate}
        </table-wrapper>

        <q1-spinner-panel label="Loading..." no-bg ?open=${!this.initComplete}></q1-spinner-panel>
      </div>

      ${!this.showLoadMoreButton ? '' : html`
        <div id="load-more-container">
          <sl-button
            id="load-more-button"
            @click=${this.loadMore}
            pill
            ?disabled=${this.loading}
          >
            ${this.loading ? html`
              <sl-spinner slot="prefix"></sl-spinner>
              Loading...
            ` : html`
              <sl-icon slot="prefix" library="material" name="more_horiz"></sl-icon>
              Load More
            `}
          </sl-button>
        </div>
      `}

      ${this.isSignedIn ? '' : html`
        <div class="not-signed-in-message">
          <a href="/signin" target="_blank">Sign in</a> to browse all projects.
        </div>
      `}
    `;
  }

  get queryTableTemplate() {
    return html`
      <table id="projects-table" class="q1">
        <thead>
          <th>Project</th>
          <th>${this.detailsTitle}</th>
        </thead>

        <tbody>
          ${!this.projects?.length ? html`
            <tr class="empty-placeholder">
              <td colspan="6">
                <span class="td-inner">
                  <sl-icon library="material" name="block"></sl-icon>
                  <span>${this.emptyPlaceholderText}</span>
                </span>
              </td>
            </tr>
          ` : repeat(this.projects, (project) => project.id, (project) => html`
            <tr>
              <td class="project-col">
                <q1-project-chip
                  .project=${project}
                  ?signed-in=${this.isSignedIn}
                  extra-fields="key,status_label"
                ></q1-project-chip>
              </td>
              <td class=${`role-col ${project.status}`}>
                <span class="role-val">
                  <sl-icon
                    library=${project.role_iconLibrary}
                    name=${project.role_icon}
                  ></sl-icon>
                  ${project.role_label}
                </span>

                ${project.status === 'active' ? '' : html`
                  <span class="status-val">
                    (${project.status_label})
                  </span>
                `}
              </td>
              <td class="details-col">
                ${project.status === 'active' ? project.headline || '' : html`
                  ${project.joinFlow_label}
                  ${!project.firstProposedBy ? '' : `by ${project.firstProposedBy.name}`}
                `}
              </td>
              <td class="date-col">
                ${!project.timestamp ? '-' : html`
                  <sl-format-date
                    date=${project.timestamp}
                    month="short" day="numeric" year="numeric"
                  ></sl-format-date>
                `}
              </td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  /*=== UTILITY METHODS ===*/

  /** Called on page load projects. */
  _enrichCachedProjects() {
    this.projects.forEach(project => {
      enrichProject(project, this.projectRefMap);
      
      if (project.key) {
        project.chipLink = `${this.subjectDetailViewPath}/${project.key}`;
      }
    });
  }
  
}

customElements.define('q1-projects-list', Q1ProjectsList);
